import { useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import { KimChronologyFact } from './kim-chronology-fact';
import { ReferenceType } from '../../../../../../../../../../../backend/src/types/types';
import {
  useGetFactDetailsForKimChronology,
  type KimChronologyFactDetail,
} from '@/api/queries/useGetFactDetailsForKimChronology';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { cn } from '@/new/lib/utils';

const INITIAL_DISPLAY_COUNT = 10;

interface KimChronologySectionProps {
  factIds: string[];
  referenceIndexMap: Map<string, { index: number; referenceType: ReferenceType }>;
  onReferenceClick?: (id: string, referenceType: ReferenceType) => void;
}

export function KimChronologySection({ factIds, referenceIndexMap, onReferenceClick }: KimChronologySectionProps) {
  const { matterId, subsetId } = useParams<{ matterId: string; subsetId: string }>();
  const [isExpanded, setIsExpanded] = useState(false);

  const { data: summaries, isLoading } = useGetFactDetailsForKimChronology(matterId, subsetId, factIds);

  const summaryMap = useMemo(() => {
    const map = new Map<string, KimChronologyFactDetail>();
    summaries?.forEach((s) => map.set(s.id, s));
    return map;
  }, [summaries]);

  if (!subsetId || factIds.length === 0) return null;

  const displayedIds =
    isExpanded || factIds.length <= INITIAL_DISPLAY_COUNT ? factIds : factIds.slice(0, INITIAL_DISPLAY_COUNT);

  const hiddenCount = factIds.length - INITIAL_DISPLAY_COUNT;

  return (
    <div className="my-8 px-2">
      <div
        className={cn(!isExpanded && hiddenCount > 0 && 'mask-b-from-[calc(100%-128px)] mask-b-to-[calc(100%-8px)]')}
      >
        {displayedIds.map((factId, index) => (
          <div key={factId} className="group relative mb-2 flex w-full px-10">
            {/* Timeline circles and lines */}
            <div className="absolute top-4.5 left-0.25 z-10 h-4.25 w-4.25 rounded-full border-2 border-teal-400 bg-teal-50"></div>
            <div
              className={cn(
                'absolute inset-0 -inset-y-1 w-px translate-x-[9px] bg-gradient-to-b from-gray-200 via-teal-400 via-[30px] to-gray-200 to-[65px] group-first:top-4.5 group-first:from-teal-400 group-first:via-[10px] group-first:to-[40px]',
                index === displayedIds.length - 1 && (isExpanded || hiddenCount <= 0) && 'bottom-[calc(100%-32px)]',
              )}
            />

            <KimChronologyFact
              factId={factId}
              summary={summaryMap.get(factId)}
              isLoading={isLoading && !summaryMap.has(factId)}
              referenceIndexMap={referenceIndexMap}
              onReferenceClick={onReferenceClick}
            />
          </div>
        ))}
      </div>

      {hiddenCount > 0 && (
        <div className="mt-3 flex justify-center">
          <Button
            variant="white"
            size="sm"
            onClick={() => setIsExpanded((prev) => !prev)}
            className="gap-1 rounded-full pl-2.75 font-sans font-normal text-gray-500"
          >
            <span>{isExpanded ? 'Show less' : `Show ${hiddenCount} more`}</span>
            <Icon className={isExpanded ? 'lucide--chevron-up' : 'lucide--chevron-down'} />
          </Button>
        </div>
      )}
    </div>
  );
}
